// STARWEAVE — the exploration / progression loop. Turns the flight sandbox into
// a game with purpose: fly within scan range of a body, SCAN it, and chart it.
// New charts award credits + a toast; charted worlds accrete into a Codex you
// can open (the discovery LOG). Everything persists to localStorage so a
// player's universe-spanning logbook survives across sessions.
//
// This module owns its own DOM (a bottom-centre SCAN button, a toast, a LOG
// overlay + its open button) and its own input (S to scan, L to toggle the
// log). It draws no Three.js — it reads the live FlightNav + current World each
// frame and emits extra HUD rows for the In-Flight panel.
//
// Self-contained: import { createDiscovery } from './discovery' in main, build
// it once, feed it update(nav, world) each frame, and merge getHudRows() into
// the In-Flight HUD. See the integration spec for the exact wiring.

import type { FlightNav } from './flight';
import type { World } from './world';

// ---------------------------------------------------------------------------
// Save model
// ---------------------------------------------------------------------------

const SAVE_KEY = 'sw_save';
const SAVE_VERSION = 1;

/** A single charted body, keyed by system index + body name. */
export interface DiscoveryEntry {
  /** Stable id: `${system}:${body}` (de-dupes re-scans of the same body). */
  id: string;
  system: number;      // hyperdrive jump index the body lives in
  body: string;        // FlightNav.nearest at scan time (e.g. "World 3", "Moon")
  label: string;       // world.label when it's the home world, else a kind tag
  kind: BodyKind;      // classification used for the credit reward + Codex icon
  credits: number;     // reward granted for this chart
  at: number;          // Date.now() when charted
}

export interface SaveData {
  v: number;
  discovered: DiscoveryEntry[];
  credits: number;
  jumps: number;       // highest system index reached
  charted: number;     // distinct systems with >=1 chart
}

type BodyKind = 'star' | 'world' | 'moon' | 'homeworld';

// Credit reward per body kind — stars are landmark finds, moons are minor.
const KIND_REWARD: Record<BodyKind, number> = {
  star: 250,
  homeworld: 180,
  world: 120,
  moon: 60,
};

const KIND_GLYPH: Record<BodyKind, string> = {
  star: '✸',
  homeworld: '◉',
  world: '○',
  moon: '·',
};

// Scan range, in km. Derived to feel reachable: the flight rig auto-slows inside
// ~2600 units (≈130,000 km) of a surface, so a generous-but-earned range here
// means you must actually fly down to a body, not snipe it from the void.
const SCAN_RANGE_KM = 85000;

// ---------------------------------------------------------------------------
// Progression tiers — each defines an objective shown in the HUD + Codex.
// ---------------------------------------------------------------------------

interface Tier {
  rank: string;        // title earned at this tier
  need: number;        // systems charted required to *reach* this tier
}

// Ranks scale with systems charted. The "objective" is always the gap to the
// NEXT tier; the current rank is the highest tier already satisfied.
const TIERS: Tier[] = [
  { rank: 'Drifter', need: 0 },
  { rank: 'Pathfinder', need: 1 },
  { rank: 'Cartographer', need: 3 },
  { rank: 'Trailblazer', need: 5 },
  { rank: 'Starfarer', need: 10 },
  { rank: 'Voyager', need: 18 },
  { rank: 'Luminary', need: 30 },
  { rank: 'Astral Sovereign', need: 50 },
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface Discovery {
  /** Feed the live nav + current world every frame. */
  update(nav: FlightNav, world: World): void;
  /** Attempt to chart the nearest body (button / 'S'). Returns the outcome. */
  onScan(): ScanResult;
  /** Toggle the Codex (discovery log) overlay (button / 'L'). */
  toggleLog(open?: boolean): void;
  /** Extra HUD rows to merge into the In-Flight panel (fed each frame). */
  getHudRows(): [string, string][];
  /** A one-line objective string (e.g. "Chart 3 systems → Cartographer"). */
  getObjective(): string;
  /** Current save snapshot (read-only copy). */
  getState(): SaveData;
  /** Remove the DOM + listeners (not used in-game, handy for hot reload). */
  dispose(): void;
}

export type ScanResult =
  | { ok: true; entry: DiscoveryEntry; firstInSystem: boolean }
  | { ok: false; reason: 'out-of-range' | 'already-charted' | 'no-target' };

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export function createDiscovery(): Discovery {
  const save = loadSave();

  // Live state, refreshed every update().
  let nav: FlightNav | null = null;
  let world: World | null = null;

  // ---- DOM: scan button, toast, log button, log overlay ----
  injectStyles();
  const scanBtn = makeScanButton();
  const toast = makeToast();
  const logBtn = makeLogButton();
  const log = makeLogOverlay();
  document.body.appendChild(scanBtn);
  document.body.appendChild(toast.el);
  document.body.appendChild(logBtn);
  document.body.appendChild(log.el);

  let logOpen = false;

  // Throttle the scan button's enabled/label refresh (no per-frame DOM thrash).
  let lastBtnState = '';

  function classify(nav: FlightNav, world: World): { body: string; kind: BodyKind; label: string } {
    const body = nav.nearest;
    // The home world's nav name is its archetype name (world.name). When the
    // nearest body is that, it's the detailed home world of this system.
    let kind: BodyKind;
    if (body === world.name) kind = 'homeworld';
    else if (body === 'Star') kind = 'star';
    else if (body === 'Moon') kind = 'moon';
    else kind = 'world';
    const label = kind === 'homeworld' ? world.label : `${cap(kind)} · System ${nav.system}`;
    return { body, kind, label };
  }

  function inRange(): boolean {
    return !!nav && nav.distKm <= SCAN_RANGE_KM;
  }

  function entryId(system: number, body: string): string {
    return `${system}:${body}`;
  }

  function isCharted(system: number, body: string): boolean {
    const id = entryId(system, body);
    return save.discovered.some((e) => e.id === id);
  }

  function recomputeCharted(): void {
    const systems = new Set<number>();
    for (const e of save.discovered) systems.add(e.system);
    save.charted = systems.size;
  }

  function onScan(): ScanResult {
    if (!nav || !world) return { ok: false, reason: 'no-target' };
    const { body, kind, label } = classify(nav, world);
    if (isCharted(nav.system, body)) {
      flashToast(`ALREADY CHARTED — ${body}`, 'muted');
      return { ok: false, reason: 'already-charted' };
    }
    if (!inRange()) {
      flashToast(`OUT OF RANGE — close within ${SCAN_RANGE_KM.toLocaleString()} km`, 'muted');
      return { ok: false, reason: 'out-of-range' };
    }
    const reward = KIND_REWARD[kind];
    const entry: DiscoveryEntry = {
      id: entryId(nav.system, body),
      system: nav.system,
      body,
      label,
      kind,
      credits: reward,
      at: Date.now(),
    };
    const systemsBefore = save.charted;
    save.discovered.push(entry);
    save.credits += reward;
    save.jumps = Math.max(save.jumps, nav.system);
    recomputeCharted();
    const firstInSystem = save.charted > systemsBefore;
    persist(save);
    rebuildLog();
    flashToast(`DISCOVERED — ${body}   +${reward} ¢`, 'reward');
    pulseScanButton();
    return { ok: true, entry, firstInSystem };
  }

  function update(n: FlightNav, w: World): void {
    nav = n;
    world = w;
    // Keep jumps in sync even if the player never scans in a new system.
    if (n.system > save.jumps) { save.jumps = n.system; persist(save); }
    refreshScanButton();
  }

  function refreshScanButton(): void {
    if (!nav || !world) return;
    const { body } = classify(nav, world);
    const charted = isCharted(nav.system, body);
    const ranged = inRange();
    const state = `${body}|${charted ? 'c' : ranged ? 'r' : 'f'}`;
    if (state === lastBtnState) return;
    lastBtnState = state;

    const label = scanBtn.querySelector<HTMLElement>('.sw-scan-label');
    const sub = scanBtn.querySelector<HTMLElement>('.sw-scan-sub');
    if (charted) {
      scanBtn.classList.remove('ready');
      scanBtn.classList.add('charted');
      if (label) label.textContent = 'CHARTED';
      if (sub) sub.textContent = body;
    } else if (ranged) {
      scanBtn.classList.add('ready');
      scanBtn.classList.remove('charted');
      if (label) label.textContent = 'SCAN';
      if (sub) sub.textContent = body;
    } else {
      scanBtn.classList.remove('ready', 'charted');
      if (label) label.textContent = 'SCAN';
      if (sub) sub.textContent = `${nav.distKm.toLocaleString()} km`;
    }
  }

  function pulseScanButton(): void {
    scanBtn.classList.remove('pulse');
    // reflow so the animation restarts
    void scanBtn.offsetWidth;
    scanBtn.classList.add('pulse');
  }

  function getObjective(): string {
    const next = TIERS.find((t) => t.need > save.charted);
    if (!next) return `Apex rank: ${currentRank()}`;
    const remain = next.need - save.charted;
    return `Chart ${remain} more system${remain === 1 ? '' : 's'} → ${next.rank}`;
  }

  function currentRank(): string {
    let rank = TIERS[0].rank;
    for (const t of TIERS) if (save.charted >= t.need) rank = t.rank;
    return rank;
  }

  function getHudRows(): [string, string][] {
    let scanStatus = '—';
    if (nav && world) {
      const { body } = classify(nav, world);
      if (isCharted(nav.system, body)) scanStatus = 'charted';
      else if (inRange()) scanStatus = 'in range — SCAN';
      else scanStatus = `${Math.max(0, nav.distKm - SCAN_RANGE_KM).toLocaleString()} km to range`;
    }
    return [
      ['Rank', currentRank()],
      ['Credits', `${save.credits.toLocaleString()} ¢`],
      ['Charted', `${save.charted} sys · ${save.discovered.length} bodies`],
      ['Scan', scanStatus],
      ['Objective', getObjective()],
    ];
  }

  // ---- Codex / log overlay ----
  function rebuildLog(): void {
    log.title.textContent = 'Star Codex';
    log.meta.textContent =
      `${currentRank()}   ·   ${save.credits.toLocaleString()} ¢   ·   ${getObjective()}`;
    if (save.discovered.length === 0) {
      log.list.innerHTML =
        '<div class="sw-log-empty">No charts yet. Fly within scan range of a body and press SCAN.</div>';
      return;
    }
    // Group by system, newest system first; bodies newest-first within.
    const bySystem = new Map<number, DiscoveryEntry[]>();
    for (const e of save.discovered) {
      const arr = bySystem.get(e.system) ?? [];
      arr.push(e);
      bySystem.set(e.system, arr);
    }
    const systems = [...bySystem.keys()].sort((a, b) => b - a);
    const parts: string[] = [];
    for (const sysIdx of systems) {
      const rows = (bySystem.get(sysIdx) ?? []).slice().sort((a, b) => b.at - a.at);
      const sysName = sysIdx === 0 ? 'Home System' : `System +${sysIdx}`;
      parts.push(`<div class="sw-log-sys">${esc(sysName)}<span>${rows.length} charted</span></div>`);
      for (const e of rows) {
        parts.push(
          `<div class="sw-log-row">` +
            `<span class="sw-log-glyph">${KIND_GLYPH[e.kind]}</span>` +
            `<span class="sw-log-name">${esc(e.body)}</span>` +
            `<span class="sw-log-tag">${esc(e.label)}</span>` +
            `<span class="sw-log-cr">+${e.credits} ¢</span>` +
          `</div>`,
        );
      }
    }
    log.list.innerHTML = parts.join('');
  }

  function toggleLog(open?: boolean): void {
    logOpen = open === undefined ? !logOpen : open;
    if (logOpen) rebuildLog();
    log.el.classList.toggle('open', logOpen);
    logBtn.classList.toggle('active', logOpen);
  }

  // ---- Toasts ----
  let toastTimer = 0;
  function flashToast(text: string, kind: 'reward' | 'muted'): void {
    toast.el.textContent = text;
    toast.el.classList.remove('reward', 'muted', 'show');
    void toast.el.offsetWidth;
    toast.el.classList.add(kind, 'show');
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.el.classList.remove('show'), 2200);
  }

  // ---- Wiring ----
  scanBtn.addEventListener('click', (e) => { e.stopPropagation(); onScan(); });
  scanBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
  logBtn.addEventListener('click', (e) => { e.stopPropagation(); toggleLog(); });
  logBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
  log.close.addEventListener('click', (e) => { e.stopPropagation(); toggleLog(false); });
  log.el.addEventListener('pointerdown', (e) => e.stopPropagation());
  // Click the dimmed backdrop (outside the card) to close.
  log.el.addEventListener('click', (e) => { if (e.target === log.el) toggleLog(false); });

  const onKey = (e: KeyboardEvent) => {
    const k = e.key.toLowerCase();
    if (k === 's') { e.preventDefault(); onScan(); }
    else if (k === 'l') { e.preventDefault(); toggleLog(); }
    else if (k === 'escape' && logOpen) { toggleLog(false); }
  };
  addEventListener('keydown', onKey);

  rebuildLog();

  return {
    update,
    onScan,
    toggleLog,
    getHudRows,
    getObjective,
    getState: () => structuredCloneSafe(save),
    dispose() {
      removeEventListener('keydown', onKey);
      scanBtn.remove();
      toast.el.remove();
      logBtn.remove();
      log.el.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

function loadSave(): SaveData {
  const fresh: SaveData = { v: SAVE_VERSION, discovered: [], credits: 0, jumps: 0, charted: 0 };
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return fresh;
    const parsed = JSON.parse(raw) as Partial<SaveData>;
    if (!parsed || !Array.isArray(parsed.discovered)) return fresh;
    return {
      v: SAVE_VERSION,
      discovered: parsed.discovered.filter(isEntry),
      credits: numOr(parsed.credits, 0),
      jumps: numOr(parsed.jumps, 0),
      charted: numOr(parsed.charted, 0),
    };
  } catch {
    return fresh;
  }
}

function persist(save: SaveData): void {
  try { localStorage.setItem(SAVE_KEY, JSON.stringify(save)); } catch { /* storage full / disabled */ }
}

function isEntry(e: unknown): e is DiscoveryEntry {
  const o = e as Partial<DiscoveryEntry> | null;
  return !!o && typeof o.id === 'string' && typeof o.body === 'string' && typeof o.system === 'number';
}

function numOr(v: unknown, d: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : d;
}

function structuredCloneSafe(s: SaveData): SaveData {
  return JSON.parse(JSON.stringify(s)) as SaveData;
}

// ---------------------------------------------------------------------------
// DOM builders
// ---------------------------------------------------------------------------

function makeScanButton(): HTMLButtonElement {
  const b = document.createElement('button');
  b.id = 'sw-scan';
  b.className = 'sw-scan';
  b.type = 'button';
  b.innerHTML =
    '<span class="sw-scan-ring"></span>' +
    '<span class="sw-scan-core"><span class="sw-scan-label">SCAN</span>' +
    '<span class="sw-scan-sub">—</span></span>';
  return b;
}

function makeToast(): { el: HTMLDivElement } {
  const el = document.createElement('div');
  el.id = 'sw-toast';
  el.className = 'sw-toast';
  return { el };
}

function makeLogButton(): HTMLButtonElement {
  const b = document.createElement('button');
  b.id = 'sw-logbtn';
  b.className = 'sw-logbtn';
  b.type = 'button';
  b.innerHTML = '<span>CODEX</span>';
  b.title = 'Discovery log (L)';
  return b;
}

interface LogOverlay {
  el: HTMLDivElement;
  title: HTMLElement;
  meta: HTMLElement;
  list: HTMLElement;
  close: HTMLElement;
}

function makeLogOverlay(): LogOverlay {
  const el = document.createElement('div');
  el.id = 'sw-log';
  el.className = 'sw-log';
  el.innerHTML =
    '<div class="sw-log-card">' +
      '<div class="sw-log-head">' +
        '<div><div class="sw-log-title">Star Codex</div>' +
        '<div class="sw-log-meta"></div></div>' +
        '<button type="button" class="sw-log-close" aria-label="Close">✕</button>' +
      '</div>' +
      '<div class="sw-log-list"></div>' +
    '</div>';
  return {
    el,
    title: el.querySelector('.sw-log-title') as HTMLElement,
    meta: el.querySelector('.sw-log-meta') as HTMLElement,
    list: el.querySelector('.sw-log-list') as HTMLElement,
    close: el.querySelector('.sw-log-close') as HTMLElement,
  };
}

// ---------------------------------------------------------------------------
// Styles — matches the index.html theme (amber accent, condensed font, dark
// glass, clip-path corner cuts, subtle blur). Injected once.
// ---------------------------------------------------------------------------

function injectStyles(): void {
  if (document.getElementById('sw-discovery-style')) return;
  const s = document.createElement('style');
  s.id = 'sw-discovery-style';
  s.textContent = `
.sw-scan {
  position: fixed; left: 50%; bottom: calc(env(safe-area-inset-bottom, 0px) + 64px);
  transform: translateX(-50%); z-index: 30; pointer-events: auto;
  width: 96px; height: 96px; border: 0; background: none; cursor: pointer;
  font-family: 'Avenir Next Condensed','SF Pro Display',system-ui,sans-serif;
  color: var(--ink, #eaf4ff); -webkit-tap-highlight-color: transparent;
  opacity: 0.78; transition: opacity .25s ease, transform .25s ease;
}
.sw-scan:active { transform: translateX(-50%) scale(0.94); }
.sw-scan .sw-scan-ring {
  position: absolute; inset: 0; border-radius: 50%;
  border: 1.5px solid rgba(255,255,255,0.28);
  box-shadow: inset 0 0 18px rgba(120,200,255,0.10);
}
.sw-scan .sw-scan-core {
  position: absolute; inset: 11px; border-radius: 50%;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  background: linear-gradient(180deg, rgba(9,16,28,0.42), rgba(9,16,28,0.66));
  -webkit-backdrop-filter: blur(7px); backdrop-filter: blur(7px);
  border: 1px solid rgba(255,255,255,0.10);
}
.sw-scan .sw-scan-label {
  font-size: 13px; letter-spacing: 3px; text-transform: uppercase; font-weight: 600;
}
.sw-scan .sw-scan-sub {
  margin-top: 3px; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase;
  opacity: 0.6; max-width: 70px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.sw-scan.ready {
  opacity: 1;
}
.sw-scan.ready .sw-scan-ring {
  border-color: var(--accent, #ffb340);
  box-shadow: 0 0 18px rgba(255,179,64,0.45), inset 0 0 18px rgba(255,179,64,0.18);
  animation: sw-scan-breathe 1.8s ease-in-out infinite;
}
.sw-scan.ready .sw-scan-label { color: var(--accent-soft, #ffd98a); }
.sw-scan.charted { opacity: 0.5; }
.sw-scan.charted .sw-scan-ring { border-color: rgba(120,200,255,0.4); animation: none; box-shadow: none; }
.sw-scan.pulse .sw-scan-ring { animation: sw-scan-pulse .6s ease-out; }
@keyframes sw-scan-breathe {
  0%,100% { transform: scale(1); } 50% { transform: scale(1.045); }
}
@keyframes sw-scan-pulse {
  0% { box-shadow: 0 0 0 0 rgba(255,179,64,0.55); }
  100% { box-shadow: 0 0 0 26px rgba(255,179,64,0); }
}

.sw-toast {
  position: fixed; left: 50%; bottom: calc(env(safe-area-inset-bottom, 0px) + 176px);
  transform: translateX(-50%) translateY(8px); z-index: 31; pointer-events: none;
  font-family: 'Avenir Next Condensed','SF Pro Display',system-ui,sans-serif;
  font-size: 13px; letter-spacing: 2.5px; text-transform: uppercase;
  padding: 9px 16px; white-space: nowrap; color: var(--ink, #eaf4ff);
  background: linear-gradient(180deg, rgba(9,16,28,0.5), rgba(9,16,28,0.72));
  -webkit-backdrop-filter: blur(7px); backdrop-filter: blur(7px);
  border-left: 2px solid var(--accent, #ffb340);
  clip-path: polygon(0 0,100% 0,100% calc(100% - 10px),calc(100% - 10px) 100%,0 100%);
  opacity: 0; transition: opacity .28s ease, transform .28s ease;
}
.sw-toast.show { opacity: 1; transform: translateX(-50%) translateY(0); }
.sw-toast.reward { color: #fff; border-left-color: var(--accent, #ffb340); text-shadow: 0 0 12px rgba(255,179,64,0.5); }
.sw-toast.muted { border-left-color: rgba(160,180,210,0.5); opacity: 0; }
.sw-toast.muted.show { opacity: 0.92; }

.sw-logbtn {
  position: fixed; right: calc(env(safe-area-inset-right, 0px) + 22px);
  bottom: calc(env(safe-area-inset-bottom, 0px) + 30px); z-index: 30; pointer-events: auto;
  font-family: 'Avenir Next Condensed','SF Pro Display',system-ui,sans-serif;
  font-size: 11px; letter-spacing: 3px; text-transform: uppercase; font-weight: 600;
  color: var(--ink, #eaf4ff); cursor: pointer; -webkit-tap-highlight-color: transparent;
  padding: 11px 16px;
  background: linear-gradient(180deg, rgba(9,16,28,0.34), rgba(9,16,28,0.58));
  -webkit-backdrop-filter: blur(7px); backdrop-filter: blur(7px);
  border: 0; border-right: 2px solid var(--accent, #ffb340);
  clip-path: polygon(13px 0,100% 0,100% 100%,0 100%,0 13px);
  opacity: 0.82; transition: opacity .2s ease;
}
.sw-logbtn:hover { opacity: 1; }
.sw-logbtn.active { opacity: 1; color: var(--accent-soft, #ffd98a); }

.sw-log {
  position: fixed; inset: 0; z-index: 50; pointer-events: none;
  display: flex; align-items: center; justify-content: center;
  background: rgba(2,4,8,0); opacity: 0; transition: opacity .25s ease;
  font-family: 'Avenir Next Condensed','SF Pro Display',system-ui,sans-serif;
}
.sw-log.open { pointer-events: auto; opacity: 1; background: rgba(2,4,8,0.55); }
.sw-log-card {
  width: min(520px, 90vw); max-height: 78vh; display: flex; flex-direction: column;
  background: linear-gradient(180deg, rgba(9,16,28,0.82), rgba(7,12,22,0.92));
  -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px);
  border-left: 2px solid var(--accent, #ffb340);
  clip-path: polygon(0 0,100% 0,100% calc(100% - 18px),calc(100% - 18px) 100%,0 100%);
  transform: translateY(14px) scale(0.98); transition: transform .25s ease;
  color: var(--ink, #eaf4ff);
}
.sw-log.open .sw-log-card { transform: none; }
.sw-log-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 18px 20px 14px; border-bottom: 1px solid rgba(255,255,255,0.08);
}
.sw-log-title { font-size: 17px; letter-spacing: 4px; text-transform: uppercase; color: #fff; }
.sw-log-meta { margin-top: 6px; font-size: 10.5px; letter-spacing: 2px; text-transform: uppercase; opacity: 0.62; color: var(--accent-soft, #ffd98a); }
.sw-log-close {
  border: 0; background: none; color: var(--ink, #eaf4ff); opacity: 0.6;
  font-size: 16px; cursor: pointer; line-height: 1; padding: 4px;
}
.sw-log-close:hover { opacity: 1; }
.sw-log-list { overflow-y: auto; padding: 8px 20px 20px; -webkit-overflow-scrolling: touch; }
.sw-log-empty { padding: 28px 4px; font-size: 12px; letter-spacing: 1.5px; opacity: 0.5; line-height: 1.7; }
.sw-log-sys {
  display: flex; justify-content: space-between; align-items: baseline;
  margin: 16px 0 6px; font-size: 11px; letter-spacing: 3px; text-transform: uppercase;
  color: var(--accent-soft, #ffd98a); opacity: 0.85;
  border-bottom: 1px solid rgba(255,179,64,0.18); padding-bottom: 4px;
}
.sw-log-sys span { font-size: 9.5px; letter-spacing: 1.5px; opacity: 0.6; color: var(--ink, #eaf4ff); }
.sw-log-row {
  display: flex; align-items: center; gap: 10px; padding: 7px 0;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.sw-log-glyph { width: 16px; text-align: center; color: var(--accent, #ffb340); font-size: 14px; }
.sw-log-name { font-size: 13px; letter-spacing: 1px; min-width: 78px; }
.sw-log-tag { flex: 1; font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; opacity: 0.5; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sw-log-cr { font-size: 12px; letter-spacing: 0.5px; color: var(--accent-soft, #ffd98a); }
`;
  document.head.appendChild(s);
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;');
}
